import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { colorIdentidad, ORDEN_ENTIDADES } from '../config/rayos';
import { formatoTitulo } from '../config/titulos';
import { resolver } from '../config/personalizacion';
import { MONEDA, usePlanCards, planCardLines, isCollaboratorPlan } from '../config/lector';
import { useModo } from '../config/modo';
import { IMAGENES_VOZ, FOTOS_VOZ } from '../config/imagenes';
import { imagenDelDia } from '../config/imagenesDeepArt';
import { useLector } from '../config/lector';
import { estadosConPiezas, temasConPiezas } from '../config/heridas';
import { useCatalog, useDailyPiece, useDoors, useReach } from '../content/hooks';
import { nombreVoz, nombreVozCorto } from '../config/nombres_voz';
import AvisoIdioma from '../components/AvisoIdioma';
import { conPunto, EN_EL_TABLERO, LOTE } from '../config/alcance';
import Emblema from '../components/Emblema';
import Umbral from '../components/Umbral';
import './home.css';

/* La frase de entrada va en UNA línea en todos los idiomas (Faina, 02-oct).
   El CSS le prohíbe partirse; si en ese idioma o en esa pantalla no cabe, se
   achica la letra en la proporción exacta que falta. Se recalcula al cambiar
   el texto (idioma), al cargar la tipografía y al cambiar el ancho. */
const useUnaLinea = (texto) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let anchoPrevio = -1;
    const ajustar = (forzar) => {
      const ancho = el.clientWidth;
      if (!forzar && ancho === anchoPrevio) return;
      anchoPrevio = ancho;
      el.style.fontSize = '';
      const necesita = el.scrollWidth;
      if (necesita > ancho) {
        const base = parseFloat(getComputedStyle(el).fontSize);
        el.style.fontSize = `${Math.floor((base * ancho / necesita) * 0.98 * 10) / 10}px`;
      }
    };
    ajustar(true);
    let vivo = true;
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => { if (vivo) ajustar(true); });
    }
    const alCambiar = () => ajustar(false);
    window.addEventListener('resize', alCambiar);
    return () => { vivo = false; window.removeEventListener('resize', alCambiar); };
  }, [texto]);
  return ref;
};

/**
 * LA PORTADA.
 *
 * 🔴 TODO SU TEXTO SALE DE LA TABLA, NO DEL CÓDIGO. Idea de Faina (26-ago) sobre
 * la tabla de traducción que ya existía: «me parece una manera eficiente de
 * inyectar las palabras que quiero. Puede ser parte del backend y yo la voy
 * modificando». Es el patrón que ya funciona en el proyecto —el Word manda en
 * las canalizaciones, el Excel manda en el tablero—, y además es lo único que
 * permite que estas palabras lleguen a los 55 idiomas de los tres sitios.
 *
 * Aquí no hay ni una frase escrita: todas se piden con t('home.…') y viven en
 * la tabla. Ver herramientas/textos.py.
 *
 * LOS BLOQUES, y qué hace cada uno:
 *
 *   ① la frase y el buscador   la puerta — sin bajada: la frase se sostiene sola
 *                              (Faina, 26-ago)
 *   ② qué te está pasando ⭐    la entrada por la HERIDA
 *   ②bis qué te gustaría saber  la entrada por la PREGUNTA (Faina, 26-ago)
 *
 * Las dos son la misma idea: nadie llega buscando «Chamuel». Llega porque le
 * duele algo o porque quiere saber algo — y esas son las dos puertas.
 *   ③ quién te habla           las catorce voces
 *   ④ hoy                      una razón para volver
 *   ⑤ qué es una canalización  la definición (Faina la pidió a Claude como
 *                              experto espiritual, 26-ago)
 *   ⑥ hasta dónde llega ⭐      LA DEF 37, HECHA VISIBLE
 *   ⑦ nuestros planes          los cuatro planes
 *   ⑧ registrarse ⭐           la unica invitacion del sitio, y va al final
 *
 * NO HAY BLOQUE DE GRACIAS (Faina, 26-ago): la portada cierra con los planes.
 * La pagina de Gracias sigue existiendo, y se llega por el menu y por el pie.
 *
 * TRABAJADORES DE LA LUZ NO TIENE BLOQUE AQUI (Faina, 26-ago): se llega por su
 * entrada del menu, y por el buscador escribiendo su nombre —que ahora los
 * encuentra los tres, porque el buscador sabe a quien va dirigida cada pieza y
 * ya no depende de si el texto dice «trabajador» o «trabajadores»—.
 *
 * La Def 37 —objetivo permanente y no negociable— manda sobre el orden: si algo
 * aumenta el número de personas que reciben una canalización, va arriba.
 */

const Home = () => {
  const { t } = useTranslation();
  const { modo } = useModo();
  const { identificado } = useLector();
  const fraseRef = useUnaLinea(t('home.frase'));
  const { items: catalogo } = useCatalog();
  const { daily } = useDailyPiece();
  const { doors } = useDoors();
  const { cuadros: CUADROS } = useReach();
  const ESTADOS_CON_PIEZAS = useMemo(() => estadosConPiezas(doors), [doors]);
  const TEMAS_CON_PIEZAS = useMemo(() => temasConPiezas(doors), [doors]);

  /**
   * Las voces, en el orden del sistema. Una sin piezas publicadas NO se
   * esconde: se ensena diciendo cuantas vienen (Los Esenios, hoy).
   */
  const porVoz = useMemo(() => ORDEN_ENTIDADES
    .map((e) => ({
      entidad: e,
      n: catalogo.filter((c) => c.entidad === e).length,
      enCamino: EN_EL_TABLERO[e] || 0
    }))
    .filter((v) => v.n > 0 || v.enCamino > 0), [catalogo]);

  /* El primer párrafo bajo el título del mensaje del día: APAGADO (Faina,
     02-oct, probado y aprobado: «ok. Así»). Quedan cara, voz y título. */
  const MOSTRAR_ENTRADA = false;

  /* La de hoy se calcula en config/deldia.js, no aqui: desde la Def 73 tambien
     la necesita la ficha —es la unica pieza que abre sin registro— y dos
     formulas separadas acabarian prometiendo una pieza y abriendo otra. */
  const deHoy = useMemo(() => (daily
    ? { ...daily, entrada: daily.parrafos.map((x) => resolver(x, {}, daily.entidad, false)).find((x) => x.trim()) || '' }
    : null), [daily]);

  /* 🖼️ La imagen de hoy de esta pieza: la misma que verá al abrirla, y otra
     mañana (Faina, 01-10-2026). Ver imagenesDeepArt.js. */
  const fotoDeHoy = useMemo(() => (deHoy ? imagenDelDia(deHoy) : null), [deHoy]);

  const planes = usePlanCards();

  return (
    <Box className="home">

      {/* ① LA ENTRADA — solo la frase, y la frase ES la puerta.
          Faina, 26-ago: «saca el buscador que hay al principio; cuando el usuario
          pinche el título lo llevará automáticamente a la biblioteca». La caja de
          buscar prometía resultados donde no los hay —el buscador vive en la
          biblioteca— y además metía un formulario entre el visitante y lo
          primero que debería ver. Ahora la portada abre con una sola frase. */}
      <Box className="home-entrada">
        <Container maxWidth="md">
          <RouterLink to="/canalizaciones" className="home-frase-enlace">
            <Typography variant="h1" className="home-frase" ref={fraseRef}>{t('home.frase')}</Typography>
          </RouterLink>
        </Container>
      </Box>

      {/* 🔴 EL ORDEN DE LA PORTADA (Faina, 29-ago). Antes era: frase · REGISTRO ·
          voces · mensaje del dia. Medido, el registro caia a 244 px y el mensaje
          del dia a 1.118: se pedia registrarse en la PRIMERA pantalla y lo unico
          que se puede leer gratis estaba en la TERCERA. Se pedia antes de dar.

          Ahora la portada dice «puede haber un mensaje para ti» y lo siguiente
          ES el mensaje; el registro llega justo despues de haberlo leido, que es
          cuando «registrate y tendras acceso a todas» se lee como consecuencia
          —acabo de leer una, hay 93 mas— y no como peaje.
          Y las voces quedan detras a proposito: acabas de leer a Miguel y
          entonces ves a las otras trece. */}
      {/* ③ LA DE HOY — EL CORAZÓN DE LA PORTADA.
          Antes era la cuarta tarjeta de ocho, del mismo tamaño que las demás, y
          era lo único de verdad que había en el home: una canalización real con
          su primer párrafo. Un sitio de canalizaciones cuya portada no deja leer
          ninguna es un folleto. Ahora ocupa su propia franja, con otro fondo, y
          el visitante lee a la entidad en los primeros segundos. */}
      {deHoy && (
      <Box className="home-franja" style={{ '--rayo': colorIdentidad(deHoy.entidad, modo) }}>
        <Container maxWidth="md">
          {/* El rotulo de la seccion, que Faina pidio nombrado (26-ago):
              el bloque se llama MENSAJE DEL DIA, y debajo va quien lo dice. */}
          <Typography className="home-rotulo home-hoy-rotulo">{t('home.hoy_titulo')}</Typography>

          {/* 🔴 EL FORMATO «LA IMAGEN MANDA» (Faina, 29-ago, elegido entre cuatro).
              El orden es: rotulo · LA FIGURA EN GRANDE · quien habla · titulo ·
              su primer parrafo · respira y entra.
              Antes la foto iba al final, despues del parrafo — o sea cuando el
              lector ya habia decidido si seguia— y a 124 px el rayo de Miguel
              era una mancha. Ahora abre, a 300, y es la que respira: una sola
              figura en la franja en vez de dos circulos compitiendo. */}
          <Umbral
            grande
            minutos={deHoy.minutos}
            rayo={colorIdentidad(deHoy.entidad, modo)}
            a={`/canalizacion/${deHoy.slug}`}
            /* La cara de quien habla hoy. Primero Deep Art (§0 del 23-sep,
               las 5 entidades del encargo); si la pieza es de otra voz, la
               foto de trabajo vieja; si tampoco, el emblema — Los Angeles y
               Los Esenios (antes de tener imagen) no tienen foto en el
               Drive, son voces colectivas y puede que nunca la tengan. */
            emblema={fotoDeHoy
              ? <img src={fotoDeHoy} alt="" className="umbral-foto" />
              : FOTOS_VOZ[deHoy.entidad]
                ? <img src={FOTOS_VOZ[deHoy.entidad]} alt="" className="umbral-foto" />
                : <Emblema entidad={deHoy.entidad} tamano={112} modo={modo} />}
          >
            <span className="home-hoy-voz">{nombreVoz(deHoy.entidad, t)}</span>
            <Typography className="home-hoy-titulo">{formatoTitulo(deHoy.titulo)}</Typography>
            {/* Sin el primer párrafo (Faina, 02-oct: «prueba sin mostrar la
                primera parte de la canalización» → «ok. Así»). Para volver,
                MOSTRAR_ENTRADA en true. */}
            {MOSTRAR_ENTRADA && (
              <Typography className="home-hoy-entrada">{deHoy.entrada}</Typography>
            )}
          </Umbral>
          <AvisoIdioma />
        </Container>
      </Box>
      )}

      {/* ①-bis EL REGISTRO, ARRIBA (Faina, 29-ago — Def 73): «es muy importante
          que cuando el oyente entre por primera vez se registre y complete los
          datos basicos, y por eso el registrame gratis debe ir al principio del
          home».
          Estaba al final, despues de los planes, y ahi solo lo veia quien ya
          habia bajado la portada entera. Desde la Def 73 el registro no es una
          invitacion amable: es LA PUERTA — sin el solo se lee el mensaje del
          dia—, asi que tiene que estar donde se ve.
          Va DESPUES de la frase de entrada, no antes: primero se dice a que se
          viene, y entonces se pide entrar. Un formulario como primera linea de
          una fundacion se lee como un peaje.
          Y no se enseña a quien ya tiene cuenta: pedirle registrarse a quien ya
          esta dentro es no saber quien es. */}
      {!identificado && (
        <Container maxWidth="lg" className="home-seccion home-seccion-registro">
          {/* 🔴 UNA SOLA FRASE (Faina, 29-ago): «este mensaje debe ser mucho mas
              directo». Antes eran un titulo y un parrafo que explicaban POR QUE
              registrarse; ahora dice QUE hay que hacer y QUE se consigue, y
              nada mas. El parrafo largo se retiro tambien de la tabla: una
              clave que ya no se usa acaba traduciendose a 32 idiomas. */}
          {/* 🔴 EL BOTON VA ARRIBA (Faina, 29-ago): primero la accion, y debajo
              lo que se consigue con ella. Y fuera «Es gratis, y siempre lo
              sera»: el propio boton dice REGISTRARME GRATIS, asi que repetirlo
              debajo era decir dos veces lo mismo en tres lineas. */}
          <Box className="home-registro">
            <span className="home-registro-luz" aria-hidden="true" />
            <RouterLink to="/entrar?registro" className="home-registro-boton">
              {t('home.registro_boton')}
            </RouterLink>
            <Typography className="home-registro-titulo">{t('home.registro_titulo')}</Typography>
          </Box>
        </Container>
      )}

      {/* ② QUIÉN TE HABLA — lo primero tras la frase (Faina, 26-ago).
          Los emblemas son lo único con color del sitio y lo más nuestro que
          tenemos: quien llega ve QUIÉN habla antes que nada. */}
      <Container maxWidth="lg" className="home-seccion">
        <Typography className="home-rotulo">{t('home.voces_titulo')}</Typography>
        <Box className="home-voces">
          {porVoz.map(({ entidad, n, enCamino }) => (
            <RouterLink
              key={entidad}
              to={n > 0 ? `/canalizaciones?voz=${encodeURIComponent(entidad)}` : '/esenios'}
              className={`home-voz${n === 0 ? ' home-voz-espera' : ''}`}
              style={{ '--rayo': colorIdentidad(entidad, modo) }}
            >
              <span className="home-voz-luz">
                {IMAGENES_VOZ[entidad]
                  ? <img src={IMAGENES_VOZ[entidad]} alt="" className="home-voz-img" />
                  : <Emblema entidad={entidad} tamano={84} modo={modo} />}
              </span>
              <span className="home-voz-nombre">{nombreVozCorto(entidad, t)}</span>
              <span className={`home-voz-n${n === 0 ? ' home-voz-n-espera' : ''}`}>
                {n > 0 ? n : t('home.en_camino', { n: enCamino })}
              </span>
            </RouterLink>
          ))}
        </Box>
      </Container>

      {/* ④ LAS DOS PUERTAS — para quien la de hoy no le habló.
          Nadie llega buscando «Chamuel»: llega porque le duele algo o porque
          quiere saber algo. */}
      <Container maxWidth="lg" className="home-seccion">
        <Box className="home-puertas">
          {[
            { rotulo: t('home.duele_titulo'), lista: ESTADOS_CON_PIEZAS },
            { rotulo: t('home.saber_titulo'), lista: TEMAS_CON_PIEZAS }
          ].map((g) => (
            <Box className="home-puerta" key={g.rotulo}>
              <Typography className="home-rotulo">{g.rotulo}</Typography>
              <Box className="home-estados">
                {g.lista.map((e) => (
                  <RouterLink key={e.id} to={`/canalizaciones?estado=${e.id}`} className="home-estado">
                    {t(e.clave)}
                    <span className="home-estado-n">{e.n}</span>
                  </RouterLink>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Container>

      {/* ⑤ NUESTRO CONTENIDO — la Def 37, visible.
          SIN el bloque «que es una canalizacion» (Faina, 26-ago: «fuera que es
          una canalizacion»). La explicacion vive en La Fundacion; la portada no
          se detiene a definir, ensena. */}
      <Container maxWidth="lg" className="home-seccion">
        <Typography className="home-rotulo">{t('home.alcance_titulo')}</Typography>
        <Box className="home-alcance">
          {/* 🟥 LO QUE HAY SOBRE LO QUE SE BUSCA, en el formato que definió
              Faina el 1-sep para «Cuántos trabajamos». Antes aquí salía solo la
              META —397 canalizaciones, 55 idiomas, 80 % de cobertura— bajo un
              rótulo que acredita hechos. Ahora delante va lo que se puede
              contar hoy y detrás, tenue, a dónde vamos. Las cifras viven en
              `config/alcance.js` → CUADROS, nunca aquí. */}
          {CUADROS.map((c) => (
            <Box className="home-cuadro" key={c.clave}>
              {/* El signo va UNA vez, al final de las dos cifras —«10%/80%» se
                  lee a trompicones— y VA EN GRIS, dentro del mismo tramo que la
                  meta (Faina, 1-sep). El naranja queda solo para el hecho: es lo
                  que hay que leer primero, y un «%» naranja se lo llevaba. */}
              <span className="home-cuadro-n">
                {c.punto ? conPunto(c.hay) : c.hay}
                {(c.meta !== undefined || c.sufijo) && (
                  <span className="home-cuadro-meta">
                    {c.meta !== undefined && `/${c.punto ? conPunto(c.meta) : c.meta}`}
                    {c.sufijo || ''}
                  </span>
                )}
              </span>
              <span className="home-cuadro-que">{t(c.clave)}</span>
            </Box>
          ))}
        </Box>
      </Container>

      {/* ⑥ EL CIERRE — los planes y la invitación, JUNTOS.
          Eran dos bloques y pedían lo mismo dos veces. El lector ve qué hay,
          cuánto cuesta y da el paso, sin volver a empezar. La invitación es la
          ÚNICA del sitio (Faina, 26-ago): fuera de las canalizaciones.
          ⚠️ Lo que promete tiene que ser verdad. Desde el 07-10-2026 el plan 1
          SÍ personaliza (Faina: el nombre dentro del texto es del registro
          gratuito), así que ofrecer «tu nombre dentro» a cambio de registrarse
          gratis ya es verdad. */}
      <Container maxWidth="lg" className="home-seccion home-cierre">
        {/* El ancla a la que apunta «Ver los planes» desde la cuenta: no hay
            pagina /planes y no hace falta —los cuatro caben aqui—, pero el
            enlace tiene que llegar a algun sitio de verdad. */}
        <Typography id="planes" className="home-rotulo ancla">{t('home.planes_titulo')}</Typography>
        <Box className="home-planes">
          {planes.map((p) => (
            <Box key={p.id} className={`home-plan${p.precio === 0 ? ' home-plan-libre' : ''}`}>
              <span className="home-plan-precio">
                {p.precio === 0 ? t('home.plan_gratis') : `${p.precio} ${MONEDA}`}
                {p.precio > 0 && <em> {t('home.plan_periodo')}</em>}
              </span>
              {/* 🟥 LO QUE INCLUYE CADA PLAN, EN VIÑETAS (Faina, 20-sep). Antes era
                  una frase por plan; ahora es la lista de lo que se entrega, y
                  cada linea lleva el numero del LOTE (297 desde el 30-sep) puesto por
                  el codigo, nunca escrito en el texto. Las lineas salen de las
                  lineas de la Def 102 por nivel (ver planCardLines). */}
              <ul className="home-plan-lista">
                {planCardLines(p).map((key) => (
                  <li key={key} className="home-plan-que">{t(key, { n: LOTE })}</li>
                ))}
              </ul>
              {/* Def 102: quien dona 297 pasa a ser colaborador de la Fundación. */}
              {isCollaboratorPlan(p) && <span className="home-plan-colaborador">{t('home.plan4_acceso')}</span>}
            </Box>
          ))}
        </Box>

      </Container>

    </Box>
  );
};

export default Home;
